import { Inject, Injectable, Injector, Optional } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgZone } from '@angular/core';
import { ActiveToast, IndividualConfig, TOAST_CONFIG, ToastrService, ToastToken } from 'ngx-toastr';
import { Overlay } from 'ngx-toastr';

/**
 * Extends ToastrService to deduplicate by title:
 * if a toast with the same title is already visible, the new one is suppressed.
 * Same-title + same-message duplicates are still counted by the global countDuplicates config.
 */
@Injectable()
export class AppToastrService extends ToastrService {
  constructor(
    @Inject(TOAST_CONFIG) token: ToastToken,
    overlay: Overlay,
    injector: Injector,
    @Optional() sanitizer: DomSanitizer,
    ngZone: NgZone
  ) {
    super(token, overlay, injector, sanitizer, ngZone);
  }

  private hasTitleActive(title?: string): boolean {
    if (!title) return false;
    return this.toasts.some(t => t.title === title);
  }

  override error(message?: string, title?: string, override?: Partial<IndividualConfig>): ActiveToast<any> {
    if (this.hasTitleActive(title)) return undefined!;
    return super.error(message, title, override);
  }

  override success(message?: string, title?: string, override?: Partial<IndividualConfig>): ActiveToast<any> {
    if (this.hasTitleActive(title)) return undefined!;
    return super.success(message, title, override);
  }

  override warning(message?: string, title?: string, override?: Partial<IndividualConfig>): ActiveToast<any> {
    if (this.hasTitleActive(title)) return undefined!;
    return super.warning(message, title, override);
  }

  override info(message?: string, title?: string, override?: Partial<IndividualConfig>): ActiveToast<any> {
    if (this.hasTitleActive(title)) return undefined!;
    return super.info(message, title, override);
  }
}
